import { useState, useEffect } from 'react';
import ProfileFormHeader from '../../../ProfileFormHeader/ProfileFormHeader';
import HalfRow from '../../../../../common/components/formElements/HalfRow';
import FormInputField from '../../../../../common/components/formElements/FormInputField';
import { CalendarIcon, MailIcon, PhoneIcon, UsersIcon, ClockIcon } from '@heroicons/react/outline';
import FullRow from '../../../../../common/components/formElements/FullRow';
import FormTextArea from '../../../../../common/components/formElements/FormTextArea';
import useAppointmentDetails from './hooks/useAppointmentDetails';
import CustomRedAlert from '../../../../../common/components/customRedAlert/CustomRedAlert';
import CustomSuccessModal from '../../../../../common/components/customSuccessModal/CustomSuccessModal';
import { Documentos } from '../../../../../v2/services/Documentos';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import Axios from '../../../../../v2/services/Axios';
import { addMinutes, mergeDateTime } from '../../../../../common/helpers/helper';
export default function HistoryAppointmentDetails(props) {
    const { appointment, onCancel, selectionMode, onSuccessfulSave } = props;
    const urlCache = new Map();
    const [openReturnAlert, setOpenReturnAlert] = useState(false);
    const [imageUrls, setImageUrls] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        const loadImages = async () => {
            if (!appointment?.documentLinks) return;

            const keys = appointment.documentLinks.split(',');
            const urls = await Promise.all(
                keys.map(async (key) => ({
                    key,
                    url: await getPresignedUrl(key, 'view')
                }))
            );

            setImageUrls(urls);
        };

        loadImages();
    }, [appointment]);

    const view = async (Key) => {
        const url = await getPresignedUrl(Key, 'view');
        window.open(url, '_blank');
    };

    const {
        handleSubmit,
        getValues,
        setValue,
        register,
        errors,
        isDirty,
        watch,
        closeErrorMessage,
        onCloseSuccessMessage,
        showSuccessModal,
        showErrorModal,
        errorBody,
        successBody,
        onSaveSubmit,
        statusOptions,
        isSubmitting
    } = useAppointmentDetails(appointment, onSuccessfulSave, selectionMode, onCancel);

    // Fetch and store Google auth status on page load
    useEffect(() => {
        const fetchGoogleStatus = async () => {
            try {
                const resp = await Axios.get('auth/google/status');
                const web = resp?.data?.web === true;
                try { localStorage.setItem('googleWebAuthed', JSON.stringify(web)); } catch (_) {}
            } catch (e) {
                try { localStorage.removeItem('googleWebAuthed'); } catch (_) {}
                console.log('Failed to fetch google status', e);
            }
        };
        fetchGoogleStatus();
    }, []);

    if (!selectionMode) {
        return null;
    }

    const _onReturnToAppointments = () => {
        if (isDirty) {
            setOpenReturnAlert(true);
        } else {
            onCancel();
        }
    };

    const _onConfirmCancelAppointment = () => {
        setOpenReturnAlert(false);
        onCancel();
    };

    const _onCancelConfirmationForClosing = () => {
        setOpenReturnAlert(false);
        onCancel();
    };

    async function getPresignedUrl(Key, Action) {
        const cachedUrl = urlCache.get(Key + Action);
        if (cachedUrl && cachedUrl.expiration > Date.now()) {
            return cachedUrl.url;
        }
        try {
            const { data } = await Documentos.url(Key, Action);
            const expirationTime = Date.now() + (Action === 'view' ? 900 : 60) * 1000;
            urlCache.set(Key + Action, { url: data, expiration: expirationTime });
            return data;
        } catch (error) {
            console.error('error in getting url:', error);
            return '';
        }
    }

    const goToPreviousRecord = () => {
        if (appointment?.idUser) {
            navigate(`/patient-previous-record/${appointment.idUser}`);
        } else if (appointment?.userEmail) {
            navigate(`/patient-previous-record/${encodeURIComponent(appointment.userEmail)}`);
        }
    };

    // Controller button for handling Google consent + submit
    const SubmitWithCalendarController = ({ disabled }) => {
        const [pending, setPending] = useState(false);
        const [code, setCode] = useState('');
        const [showConsent, setShowConsent] = useState(false);

        const login = useGoogleLogin({
            scope: 'https://www.googleapis.com/auth/calendar.events',
            flow: 'auth-code',
            accessType: 'offline',
            onSuccess: (cr) => setCode(cr?.code || ''),
            onError: () => setCode('')
        });

        const submitForm = async (calendarData = null) => {
            try {
                await handleSubmit((data) => onSaveSubmit(data, calendarData))();
            } catch (_) {}
        };

        const handleClick = async () => {
            // Only apply calendar flow for pending selection (value '1')
            const selectedStatus = String(watch('idAppointmentStatus'));
            if (selectedStatus !== '1') {
                await submitForm();
                return;
            }

            let stored = null;
            try { stored = JSON.parse(localStorage.getItem('googleWebAuthed')); } catch (_) {}

            if (stored === true) {
                const start_date = mergeDateTime(getValues()?.appDate, getValues()?.appTime);
                const end_date = addMinutes(mergeDateTime(getValues()?.appDate, getValues()?.appTime), 30);
                await submitForm({ start_date, end_date });
                return;
            }
            setShowConsent(true);
        };

        useEffect(() => {
            if (pending && code) {
                (async () => {
                    const start_date = mergeDateTime(getValues()?.appDate, getValues()?.appTime);
                    const end_date = addMinutes(mergeDateTime(getValues()?.appDate, getValues()?.appTime), 30);
                    const authCode = code;
                    setPending(false);
                    setCode('');
                    await submitForm({ code: authCode, start_date, end_date });
                })();
            }
        }, [pending, code]);

        return (
            <>
                <button
                    type="button"
                    onClick={handleClick}
                    disabled={disabled || isSubmitting}
                    className="bg-dl-red border border-transparent rounded-full shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-[#f16782] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dl-accent disabled:opacity-75"
                >
                    Cerrar
                </button>

                {showConsent && (
                    <CustomRedAlert
                        open={showConsent}
                        title={'Permiso de Google necesario'}
                        bodyText={'Necesitamos acceso a tu cuenta de Google para sincronizar el evento con tu calendario. ¿Deseas continuar?'}
                        mainActionText={'Sí'}
                        secondaryActionText={'No'}
                        onMainAction={() => { setShowConsent(false); setPending(true); login(); }}
                        onSecondaryAction={async () => { setShowConsent(false); await submitForm(); }}
                        onClose={() => setShowConsent(false)}
                    />
                )}
            </>
        );
    };

    return (
        <form onSubmit={(e) => e.preventDefault()}>
            {isSubmitting && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="bg-white rounded-lg p-8 flex flex-col items-center gap-4">
                        <div className="w-16 h-16 border-t-4 border-b-4 border-blue-500 rounded-full animate-spin"></div>
                        <p className="text-lg font-medium text-gray-700">Guardando cambios...</p>
                    </div>
                </div>
            )}
            <div className="shadow sm:rounded-md sm:overflow-hidden">
                <div className="bg-white py-6 px-4 space-y-6 sm:p-6">
                    {selectionMode !== 'viewOnly' && (
                        <ProfileFormHeader
                            title={selectionMode === 'complete' ? 'Completar Cita' : 'Solicitar Reprogramación'}
                            textColor={selectionMode === 'reschedule' ? 'text-dl-red' : null}
                        />
                    )}
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg leading-6 font-medium text-dl-secondary">
                            Detalles del paciente
                        </h3>
                        <button
                            type="button"
                            className="custome-bg text-white px-4 py-2 rounded-full hover:bg-blue-700"
                            onClick={goToPreviousRecord}
                        >
                            Registro Anterior
                        </button>
                    </div>

                    <div className="grid grid-cols-6 gap-6">
                        <HalfRow>
                            <FormInputField
                                identifier="userFullName"
                                label="Nombre del Paciente"
                                iconComponent={<UsersIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
                                type="string"
                                registration={register('userFullName', {
                                    required: appointment?.idUser ? true : false,
                                })}
                                disabled
                            />
                        </HalfRow>
                        <HalfRow>
                            <FormInputField
                                identifier="birthDate"
                                label="Fecha de Nacimiento"
                                iconComponent={<CalendarIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
                                type="date"
                                registration={register('birthDate', {
                                    required: appointment?.idUser ? true : false,
                                })}
                                disabled={appointment?.idUser}
                            />
                        </HalfRow>

                        <HalfRow>
                            <FormInputField
                                identifier="appDate"
                                label="Fecha de cita"
                                iconComponent={<CalendarIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
                                type="string"
                                registration={register('appDate', {
                                    required: appointment?.idUser ? true : false,
                                })}
                                disabled
                            />
                        </HalfRow>
                        <HalfRow>
                            <FormInputField
                                identifier="appTime"
                                label="Hora de cita"
                                iconComponent={<ClockIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
                                type="string"
                                registration={register('appTime', {
                                    required: false,
                                })}
                                disabled
                            />
                        </HalfRow>
                        <HalfRow>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                            <PhoneInput
                                country={'us'}
                                value={getValues('userPhone')}
                                onChange={(value) => setValue('userPhone', value, { shouldValidate: true })}
                                inputProps={{ name: 'userPhone' }}
                                disabled={appointment?.idUser}
                                containerClass="w-full"
                                inputClass="!w-full !h-10 !rounded-full !border !border-gray-300 !pl-12"
                            />
                        </HalfRow>
                        <HalfRow>
                            <FormInputField
                                identifier="userEmail"
                                label="Correo Electrónico"
                                iconComponent={<MailIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}
                                type="string"
                                registration={register('userEmail', {
                                    required: appointment?.idUser ? true : false,
                                })}
                                disabled
                            />
                        </HalfRow>
                        <FullRow>
                            <FormInputField
                                identifier="motiveName"
                                label="Motivo de consulta"
                                type="string"
                                registration={register('motiveName', {
                                    required: appointment?.idUser ? true : false,
                                })}
                                disabled
                            />
                            {errors.phone ? (
                                <p className="text-red-700 font-light">Este campo no puede ser vacío</p>
                            ) : null}
                        </FullRow>
                        <FullRow>
                            <FormTextArea
                                label={'Notas del paciente'}
                                identifier={'userNotes'}
                                rows={3}
                                registration={register('userNotes')}
                                disabled
                            />
                        </FullRow>
                        <FullRow>
                            <FormTextArea
                                label={
                                    selectionMode === 'completion' || selectionMode === 'viewOnly'
                                        ? 'Notas del Doctor'
                                        : 'Mensaje al usuario'
                                }
                                placeholder={
                                    selectionMode === 'reschedule'
                                        ? `Ej. Por motivos de viaje no podre atenderle, por favor reprograme para después del 20 de octubre. Muchas gracias.`
                                        : null
                                }
                                identifier={'doctorNotes'}
                                rows={3}
                                registration={register('doctorNotes', { required: true })}
                                // helperText={'Requerido para marcar como completado o reprogramar'}
                                errorMessage={errors.doctorNotes ? 'Este campo es requerido' : null}
                                disabled={appointment.statusName !== 'pending'}
                            />
                        </FullRow>
                        <FullRow>
                            {/* <FormInputField
                                identifier="statusName"
                                label="Estado actual de cita"
                                type="string"
                                registration={register('statusName', {
                                    required: true
                                })}
                                disabled
                            /> */}
                            {/* <div>
                                <label htmlFor={"statusName"}>Estado actual de cita</label>
                                <div className="relative rounded-md ">
                                    <select
                                        id={"statusName"}
                                        className='block w-full border border-gray-300 rounded-full  py-2 px-3 focus:outline-none focus:ring-dl-primary-400 focus:border-dl-primary-400 sm:text-sm font-normal h-10'
                                        {...register('statusName', { required: true })}
                                    // defaultValue=""
                                    >
                                        <option value="" disabled>Select an option</option>
                                        {statusOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div> */}
                            {/* <FormSelectDrop
                                label={'Estado actual de cita'}
                                identifier={'idAppointmentStatus'}
                                options={statusOptions}
                                defaultValue={getValues()?.idAppointmentStatus}
                                helperText={'Requerido'}
                                registration={register('idAppointmentStatus', { required: true })}
                                errorMessage={errors.idAppointmentStatus ? 'Este campo es requerido' : null}
                            />
                        </FullRow> */}
                            <label htmlFor="idAppointmentStatus" className="block text-sm font-medium text-gray-700">
                                Estado actual de cita
                            </label>
                            <select
                                id="idAppointmentStatus"
                                name="idAppointmentStatus"
                                value={watch('idAppointmentStatus')}
                                {...register('idAppointmentStatus', { required: appointment?.idUser ? true : false, })}
                                className="mt-1 block w-full bg-white border border-gray-300 rounded-full shadow-sm py-2 px-3 focus:outline-none focus:ring-dl-primary-500 font-normal focus:border-dl-primary-500 sm:text-sm"
                                disabled={appointment.statusName !== 'pending'}
                            >
                                <option key="empty-select-value" value="">
                                    --- Seleccione --
                                </option>
                                {statusOptions &&
                                    statusOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                            </select>
                            {errors.idAppointmentStatus && (
                                <p className="mt-2 text-sm text-red-700 text-normal">Este campo es requerido</p>
                            )}
                        </FullRow>
                        <FullRow>
                            <label htmlFor="documentLinks" className="block text-sm font-medium text-gray-700">
                                Documentos del paciente
                            </label>


                            {appointment?.documentLinks ? (
                                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                    {imageUrls && imageUrls.map(({ key, url }, index) => (
                                        <div key={index} className="cursor-pointer" onClick={() => view(key)}>
                                            <img
                                                src={url}
                                                alt={`Document ${index + 1}`}
                                                className="w-full h-40 object-cover rounded shadow"
                                            />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="block text-sm font-medium text-gray-700 mt-2">
                                    No hay documentos cargados para este paciente.
                                </p>
                            )}
                        </FullRow>
                    </div>
                </div>
                <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                    {/*<button
                        type="button"
                        onClick={onCancel}
                        className="bg-dl-red border border-transparent rounded-full shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-dl-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dl-red"
                    >
                        Cerrar
                    </button>*/}
                    <button
                        type="button"
                        onClick={_onReturnToAppointments}
                        className="mr-2 rounded-full border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-dl-accent focus:ring-offset-2"
                    >
                        Regresar
                    </button>

                    {appointment.statusName == 'pending' ? (
                        <GoogleOAuthProvider clientId={process.env.REACT_APP_CLIENT_ID}>
                            <SubmitWithCalendarController disabled={false} />
                        </GoogleOAuthProvider>
                    ) : ('')}
                    {/* {selectionMode === 'complete' && (
                        <button
                            type="submit"
                            className="bg-dl-primary-900 border border-transparent rounded-full shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-dl-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dl-primary-700"
                        >
                            Completar Cita
                        </button>
                    )}

                    {selectionMode === 'reschedule' && (
                        <button
                            type="submit"
                            className="bg-dl-red border border-transparent rounded-full shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-[#f16782] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dl-accent"
                        >
                            Reprogramar Cita
                        </button>
                    )} */}
                </div>
            </div>

            {openReturnAlert && (
                <CustomRedAlert
                    open={openReturnAlert}
                    title={'¿Desea descartar los cambios?'}
                    bodyText={'La cita ha sido modificada, por favor confirme que desea salir de esta vista.'}
                    onClose={_onCancelConfirmationForClosing}
                    mainActionText={'Salir'}
                    onMainAction={_onConfirmCancelAppointment}
                    secondaryActionText={'Cancelar'}
                    onSecondaryAction={_onCancelConfirmationForClosing}
                />
            )}

            {showErrorModal && (
                <CustomRedAlert
                    open={showErrorModal}
                    title={'Ocurrió un error.'}
                    bodyText={errorBody}
                    onClose={closeErrorMessage}
                />
            )}

            {showSuccessModal && (
                <CustomSuccessModal
                    title={'Registro actualizado con éxito'}
                    bodyText={successBody}
                    buttonText={'Aceptar'}
                    buttonAction={onCloseSuccessMessage}
                    open={showSuccessModal}
                    onClose={onCloseSuccessMessage}
                />
            )}
        </form>
    );
}
//
